/**
 * AssignedApp wire shape — what `GET /api/me/v1/me/apps` returns.
 *
 * Canonical for the frontline + portal surfaces. Lifted from
 * apps/portal/src/features/apps/schemas.ts as part of Milestone D of
 * the badge-signin end-to-end runtime design
 * (docs/product-areas.md). Honors
 * the prior commitment in frontline-surface-collapse.md§10.
 *
 * Why Zod (not a TS interface): consumers parse the response with a
 * cross-origin fetch, which can return drift if the backend changes.
 * The Zod parse validates at the seam — exactly what the workspace's
 * strict-TS posture (noUncheckedIndexedAccess + verbatimModuleSyntax)
 * is meant to enforce.
 */

import { z } from "zod";

export const assignedAppSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  // `private_access`: an application reached at its own name through the Edge's private door.
  protocol: z.enum(["oidc", "saml", "smart_on_fhir", "private_access"]),
  icon_url: z.string().optional(),
  launch_url: z.string(),
  // A private-access tile launches through Core first, so the relay admits the person's network
  // location before the browser navigates.
  private_access_resource_id: z.string().uuid().optional(),
});

export type AssignedApp = z.infer<typeof assignedAppSchema>;

export const assignedAppsResponseSchema = z.object({
  items: z.array(assignedAppSchema),
});

export type AssignedAppsResponse = z.infer<typeof assignedAppsResponseSchema>;

/**
 * appLaunchHref — where an application tile actually points.
 *
 * The backend now stamps the correct launch URL for every protocol:
 * - SAML apps without an explicit `launch_url` get `/saml/{id}/sso`
 *   (the universal SAML entry point — SP-initiated when SAMLRequest
 *   is present, IdP-initiated when absent).
 * - SMART apps always get `/smart/{id}/launch`.
 * - All other apps use the operator-supplied `launch_url`.
 *
 * The `apiBase` parameter is kept for backward compatibility but is
 * no longer used — URL construction has moved to the backend so the
 * tile href is correct across tenant host rebinds.
 */
export function appLaunchHref(app: AssignedApp, _apiBase?: string): string {
  return app.launch_url;
}
